import React, { useState } from "react"
import { track } from "../../analytics/mixpanel"
import { WizardStepper } from "./WizardStepper"
import { ShopifyConnectForm } from "../shopify/ShopifyConnectForm"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

// 3-step first-run flow: (1) org details, (2) connect Shopify Partner API
// [skippable], (3) done. Local state only.
export function PartnerOnboardingWizard({ createPath, connectPath, copy }) {
  const [step, setStep] = useState(0)
  const [name, setName] = useState("")
  const [partnerId, setPartnerId] = useState(null)
  const [nextPath, setNextPath] = useState("/")
  const [connectedApi, setConnectedApi] = useState(false)
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(false)

  const steps = [copy.steps.details, copy.steps.connect, copy.steps.done]

  const createPartner = async (e) => {
    e.preventDefault()
    if (!name.trim() || loading) return
    setLoading(true)
    setErrors([])
    try {
      const res = await fetch(createPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ partner: { name: name.trim() } }),
      })
      const data = await res.json()
      if (res.ok) {
        setPartnerId(data.partner_id)
        setNextPath(data.next || "/")
        setStep(1)
      } else {
        setErrors(data.errors || [copy.errors.generic])
      }
    } catch {
      setErrors([copy.errors.generic])
    } finally {
      setLoading(false)
    }
  }

  // On success the server has already probed and verified the token, so we
  // advance straight to done. The credential form lives in ShopifyConnectForm,
  // shared with the settings page.
  const onConnected = () => {
    setConnectedApi(true)
    finish(true)
  }

  const finish = (connected = connectedApi) => {
    track("Partner Onboarding Completed", { partner_id: partnerId, connected_api: connected })
    setErrors([])
    setStep(2)
  }

  const inputClass =
    "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-600"
  const primaryBtn =
    "inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"

  return (
    <main className="mx-auto max-w-lg px-4 py-12">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>

      <div className="mt-8">
        <WizardStepper steps={steps} current={step} />
      </div>

      {step === 0 && (
        <form onSubmit={createPartner} className="mt-8 flex flex-col gap-4">
          <div>
            <label htmlFor="partner-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {copy.details.nameLabel}
            </label>
            <input
              id="partner-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={copy.details.namePlaceholder}
              className={inputClass}
            />
          </div>
          {errors.length > 0 && (
            <ul className="list-inside list-disc text-xs text-red-500 dark:text-red-400">
              {errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          )}
          <button type="submit" disabled={loading || !name.trim()} className={primaryBtn}>
            {loading ? copy.details.creating : copy.details.continue}
          </button>
        </form>
      )}

      {step === 1 && (
        <div className="mt-8 flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.connect.title}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{copy.connect.body}</p>
          <ShopifyConnectForm
            connectPath={connectPath}
            copy={{
              orgLabel: copy.connect.orgLabel,
              orgPlaceholder: copy.connect.orgPlaceholder,
              tokenLabel: copy.connect.tokenLabel,
              tokenPlaceholder: copy.connect.tokenPlaceholder,
              tokenHint: copy.connect.tokenHint,
              submit: copy.connect.submit,
              connecting: copy.connect.connecting,
              errorGeneric: copy.errors.generic,
            }}
            partnerId={partnerId}
            onSuccess={onConnected}
            onCancel={() => finish(false)}
            cancelLabel={copy.connect.skip}
          />
        </div>
      )}

      {step === 2 && (
        <div className="mt-8 flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.finish.title}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{copy.finish.body}</p>
          <a href={nextPath} className={primaryBtn}>
            {copy.finish.cta}
          </a>
        </div>
      )}
    </main>
  )
}
